// ============================================================
// home-search/results-renderer.js — Render de tarjetas de resultados
// ============================================================

import { withBackendOrigin, safeUrl, normalizeText } from './utils.js';
import { getImageUrl } from '../api-config.js';
import { isPremium, getPremiumLabel } from './premium-detector.js';
import { createVerifiedBadgeElement } from './premium-card.js';
import { createSocialMediaIconsElement, createContentLabelChipElement } from '../business-ui-shared.js';

/**
 * Crea elemento DOM de estrellas para rating
 */
export function createStarsElement(rating) {
  const actualRating = (rating === null || rating === undefined || rating === 0) ? 5 : Number(rating);
  const full = Math.floor(actualRating);
  const half = actualRating - full >= 0.5;
  const frag = document.createDocumentFragment();
  for (let i = 0; i < 5; i++) {
    const icon = document.createElement('i');
    if (i < full) {
      icon.className = 'fa fa-star';
    } else if (i === full && half) {
      icon.className = 'fa fa-star-half-alt';
    } else {
      icon.className = 'fa fa-star far';
    }
    frag.appendChild(icon);
  }
  return frag;
}

let contenedorResultados = null;

/**
 * Obtiene (o crea) el contenedor de resultados debajo del hero
 */
function getResultadosContainer() {
  if (contenedorResultados) return contenedorResultados;

  contenedorResultados = document.getElementById('resultados-busqueda-inteligente');
  if (!contenedorResultados) {
    contenedorResultados = document.createElement('div');
    contenedorResultados.id = 'resultados-busqueda-inteligente';
    contenedorResultados.style.cssText = 'max-width:800px;margin:2rem auto;background:#fff;border-radius:1.5rem;box-shadow:0 8px 32px rgba(74,144,226,0.08);padding:2rem;display:none;';
    const hero = document.querySelector('.hero');
    if (hero) {
      hero.insertAdjacentElement('afterend', contenedorResultados);
    }
  }
  return contenedorResultados;
}

/**
 * Renderiza los resultados de búsqueda
 */
export function renderResultados({ results, etiquetas_encontradas, message }) {
  const container = getResultadosContainer();
  container.replaceChildren();

  if (results && results.length > 0) {
    container.style.display = 'block';

    const h2 = document.createElement('h2');
    h2.style.cssText = 'color:#2563eb;margin-bottom:1.5rem;text-align:center;';
    h2.textContent = 'Negocios encontrados';

    const grid = document.createElement('div');
    grid.className = 'search-results-grid';
    grid.style.cssText = 'display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:1.5rem;margin-bottom:2rem;';

    container.appendChild(h2);
    container.appendChild(grid);

    results.forEach((negocio, index) => {
      const card = document.createElement('div');
      card.className = 'search-result-card';
      try { card.setAttribute('data-biz-name-norm', normalizeText(negocio.name || '')); } catch(_) {}
      card.style.cssText = `
        background: white;
        border-radius: 1rem;
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        overflow: hidden;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        cursor: pointer;
      `;

      // Banner URL
      let bannerUrl = 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
      if (negocio.banner_url) {
        const raw = String(negocio.banner_url).trim();
        const isAbsolute = /^https?:\/\//i.test(raw);
        if (isAbsolute) {
          bannerUrl = raw;
        } else if (raw.startsWith('/uploads/')) {
          bannerUrl = withBackendOrigin(raw);
        } else if (!raw.includes('/')) {
          bannerUrl = withBackendOrigin(`/uploads/banners/${raw}`);
        } else {
          bannerUrl = withBackendOrigin(getImageUrl(raw));
        }
      }

      const derivedBanner = negocio.id ? withBackendOrigin(getImageUrl(`/uploads/banners/${negocio.id}.png`)) : 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
      const derivedBannerSafe = safeUrl(derivedBanner) || '';

      const isAbsoluteBanner = /^https?:\/\//i.test(bannerUrl);
      const finalBannerUrl = isAbsoluteBanner ? bannerUrl : `${bannerUrl}${bannerUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;

      // Logo URL
      let logoUrl = 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
      if (negocio.logo_url) {
        const raw = String(negocio.logo_url).trim();
        const isAbsolute = /^https?:\/\//i.test(raw);
        if (isAbsolute) {
          logoUrl = raw;
        } else if (raw.startsWith('/uploads/')) {
          logoUrl = withBackendOrigin(raw);
        } else if (!raw.includes('/')) {
          logoUrl = withBackendOrigin(`/uploads/logos/${raw}`);
        } else {
          logoUrl = withBackendOrigin(getImageUrl(raw));
        }
        logoUrl = `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
      }

      const ratingNum = Number(negocio.rating);
      const ratingDisplay = (Number.isFinite(ratingNum) && ratingNum > 0) ? ratingNum : 5;
      const ratingText = ratingDisplay.toFixed(1);
      const reviewCountNum = Number.parseInt(String(negocio.review_count ?? ''), 10);
      const reviewCountText = (Number.isFinite(reviewCountNum) && reviewCountNum >= 0) ? String(reviewCountNum) : '0';

      const bizName = String(negocio.name || 'Negocio');
      const bizDesc = String(negocio.description || 'Descripción no disponible');
      const bizCity = String(negocio.city || 'Ciudad');
      const bizCat = String(negocio.subcategory || negocio.category || 'Negocio');
      const premiumLabel = String(getPremiumLabel(negocio) || 'PREMIUM');

      const bannerSafe = safeUrl(finalBannerUrl) || 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
      const logoSafe = safeUrl(logoUrl) || 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';

      const phoneRaw = negocio.phone || '';
      const phoneDigits = String(phoneRaw || '').replace(/[^\d+]/g, '');
      const phoneHref = phoneDigits ? `tel:${phoneDigits}` : '';
      const bizIdEnc = encodeURIComponent(String(negocio.id || ''));
      const bizNameEnc = encodeURIComponent(bizName);

      // --- TOP: banner + badge + logo ---
      const top = document.createElement('div');
      top.style.cssText = 'position:relative;height:200px;overflow:hidden;';

      if (isPremium(negocio)) {
        const badge = document.createElement('div');
        badge.className = 'premium-badge premium-badge--card premium-badge--shine';
        badge.style.cssText = 'position:absolute;top:12px;left:12px;z-index:10;';

        const svgNS = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(svgNS, 'svg');
        svg.classList.add('premium-badge__icon');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('fill', 'currentColor');
        const path = document.createElementNS(svgNS, 'path');
        path.setAttribute('d', 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z');
        svg.appendChild(path);

        const txt = document.createElement('span');
        txt.className = 'premium-badge__text';
        txt.textContent = premiumLabel;

        badge.appendChild(svg);
        badge.appendChild(txt);
        top.appendChild(badge);
      }

      const bannerImg = document.createElement('img');
      bannerImg.className = 'search-banner';
      bannerImg.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      bannerImg.src = bannerSafe;
      bannerImg.width = 400;
      bannerImg.height = 250;
      bannerImg.loading = 'lazy';
      bannerImg.setAttribute('data-derived-banner', encodeURIComponent(derivedBannerSafe));
      bannerImg.alt = bizName;
      top.appendChild(bannerImg);

      const catSpan = document.createElement('span');
      catSpan.style.cssText = `position:absolute;top:0.75rem;${isPremium(negocio) ? 'right' : 'left'}:0.75rem;background:rgba(37,99,235,0.9);color:white;padding:0.25rem 0.75rem;border-radius:1rem;font-size:0.8rem;font-weight:500;`;
      catSpan.textContent = bizCat;
      top.appendChild(catSpan);

      const logoThumb = document.createElement('div');
      logoThumb.className = 'vitrina-logo-thumb';
      logoThumb.style.cssText = 'position:absolute;left:12px;bottom:90px;width:56px;height:56px;border-radius:50%;overflow:hidden;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.15);background:#f9fafb;display:flex;align-items:center;justify-content:center;z-index:999;';
      const logoImg = document.createElement('img');
      logoImg.className = 'search-logo';
      logoImg.src = logoSafe;
      logoImg.alt = `Logo ${bizName}`;
      logoImg.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      logoThumb.appendChild(logoImg);
      top.appendChild(logoThumb);

      // --- BODY ---
      const body = document.createElement('div');
      body.style.cssText = 'padding:1.25rem;padding-top:2rem;';

      const h3 = document.createElement('h3');
      h3.style.cssText = 'margin:20px 0 0.5rem 0;padding-top:10px;font-size:1.1rem;font-weight:600;color:#1f2937;display:flex;align-items:center;justify-content:space-between;gap:10px;';
      const nameWrap = document.createElement('span');
      nameWrap.style.cssText = 'display:inline-flex;align-items:center;gap:10px;min-width:0;';
      nameWrap.appendChild(document.createTextNode(bizName));
      if (negocio.verified_badge) {
        nameWrap.appendChild(createVerifiedBadgeElement());
      }
      h3.appendChild(nameWrap);
      try {
        const chip = createContentLabelChipElement(negocio);
        if (chip) {
          chip.style.marginLeft = '12px';
          chip.style.marginRight = '4px';
          h3.appendChild(chip);
        }
      } catch (_) {}

      const desc = document.createElement('p');
      desc.style.cssText = 'margin:0 0 0.75rem 0;color:#6b7280;font-size:0.9rem;line-height:1.4;';
      desc.textContent = bizDesc;

      const rowCity = document.createElement('div');
      rowCity.style.cssText = 'display:flex;align-items:center;margin-bottom:0.5rem;color:#6b7280;font-size:0.85rem;';
      const cityIcon = document.createElement('i');
      cityIcon.className = 'fa fa-map-marker-alt';
      cityIcon.style.cssText = 'margin-right:0.5rem;color:#ef4444;';
      rowCity.appendChild(cityIcon);
      const cityText = document.createElement('span');
      cityText.textContent = bizCity;
      rowCity.appendChild(cityText);

      const rowPhone = document.createElement('div');
      rowPhone.style.cssText = 'display:flex;align-items:center;margin-bottom:0.75rem;color:#6b7280;font-size:0.85rem;';
      const phoneIcon = document.createElement('i');
      phoneIcon.className = 'fa fa-phone';
      phoneIcon.style.cssText = 'margin-right:0.5rem;color:#10b981;';
      rowPhone.appendChild(phoneIcon);
      if (phoneHref) {
        const phoneLink = document.createElement('a');
        phoneLink.className = 'biz-phone-link';
        phoneLink.href = phoneHref;
        phoneLink.style.cssText = 'color:#10b981;text-decoration:none;cursor:pointer;';
        phoneLink.setAttribute('data-biz-id', bizIdEnc);
        phoneLink.setAttribute('data-biz-name', bizNameEnc);
        phoneLink.textContent = phoneRaw;
        rowPhone.appendChild(phoneLink);
      } else {
        const noPhone = document.createElement('span');
        noPhone.textContent = 'Teléfono no disponible';
        rowPhone.appendChild(noPhone);
      }

      const ratingRow = document.createElement('div');
      ratingRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;';
      const ratingLeft = document.createElement('div');
      ratingLeft.style.cssText = 'display:flex;align-items:center;';
      const starsWrap = document.createElement('span');
      starsWrap.appendChild(createStarsElement(ratingDisplay));
      const ratingNumEl = document.createElement('span');
      ratingNumEl.style.cssText = 'margin-left:0.5rem;font-size:0.85rem;color:#6b7280;';
      ratingNumEl.textContent = ratingText;
      ratingLeft.appendChild(starsWrap);
      ratingLeft.appendChild(ratingNumEl);
      const ratingCount = document.createElement('span');
      ratingCount.style.cssText = 'font-size:0.8rem;color:#9ca3af;';
      ratingCount.textContent = `(${reviewCountText} reseñas)`;
      ratingRow.appendChild(ratingLeft);
      ratingRow.appendChild(ratingCount);

      const socialWrap = document.createElement('div');
      socialWrap.className = 'biz-social-wrap';
      socialWrap.style.cssText = 'margin-top:0.75rem;';

      const likeRow = document.createElement('div');
      likeRow.style.cssText = 'display:flex;align-items:center;justify-content:flex-end;margin-top:0.5rem;';
      const bizId = negocio.id || negocio.business_id || '';
      if (bizId && typeof window.LikesUI !== 'undefined') {
        const likeOptions = {};
        if (typeof negocio.likes_count === 'number') likeOptions.count = negocio.likes_count;
        const likeBtn = window.LikesUI.createLikeButton(bizId, likeOptions);
        if (likeBtn) likeRow.appendChild(likeBtn);
      }

      body.appendChild(h3);
      body.appendChild(desc);
      body.appendChild(rowCity);
      body.appendChild(rowPhone);
      body.appendChild(ratingRow);
      body.appendChild(likeRow);
      body.appendChild(socialWrap);

      if (isPremium(negocio) && negocio.slug) {
        const ctaWrap = document.createElement('div');
        ctaWrap.style.cssText = 'margin-top:0.75rem;';
        const a = document.createElement('a');
        a.href = `/vitrina-plus.html?slug=${encodeURIComponent(negocio.slug)}`;
        a.style.cssText = 'display:inline-flex;align-items:center;gap:0.5rem;background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);color:white;text-decoration:none;padding:0.5rem 1rem;border-radius:0.5rem;font-weight:600;font-size:0.85rem;transition:all 0.3s ease;box-shadow:0 2px 8px rgba(102,126,234,0.3);';
        a.className = 'biz-vitrina-btn';
        a.appendChild(document.createTextNode('Vitrina+ '));
        const i = document.createElement('i');
        i.className = 'fas fa-arrow-right';
        a.appendChild(i);
        ctaWrap.appendChild(a);
        body.appendChild(ctaWrap);
      }

      card.appendChild(top);
      card.appendChild(body);

      // Redes sociales
      try {
        const sw = card.querySelector('.biz-social-wrap');
        if (sw) {
          const socialEl = createSocialMediaIconsElement(negocio.social_media, negocio.id, negocio.name);
          if (socialEl) {
            sw.appendChild(socialEl);
          } else {
            sw.remove();
          }
        }
      } catch (_) { /* noop */ }

      // Tracking click teléfono
      try {
        const phoneLink = card.querySelector('a.biz-phone-link');
        if (phoneLink) {
          phoneLink.addEventListener('click', () => {
            try {
              const id = decodeURIComponent(phoneLink.dataset.bizId || '');
              const nm = decodeURIComponent(phoneLink.dataset.bizName || '');
              if (window.pyaTracker && typeof window.pyaTracker.trackPhoneClick === 'function') {
                window.pyaTracker.trackPhoneClick(id, nm);
              }
            } catch {}
          });
        }
      } catch {}

      // Hover vitrina+
      try {
        const vb = card.querySelector('a.biz-vitrina-btn');
        if (vb) {
          vb.addEventListener('mouseover', () => { vb.style.transform = 'translateY(-2px)'; vb.style.boxShadow = '0 4px 12px rgba(102,126,234,0.4)'; });
          vb.addEventListener('mouseout', () => { vb.style.transform = 'translateY(0)'; vb.style.boxShadow = '0 2px 8px rgba(102,126,234,0.3)'; });
        }
      } catch {}

      // Hover card
      card.addEventListener('mouseenter', () => {
        card.style.transform = 'translateY(-4px)';
        card.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = 'translateY(0)';
        card.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      });

      grid.appendChild(card);

      // Error handlers para imágenes
      try {
        const bannerEl = card.querySelector('img.search-banner');
        if (bannerEl) {
          let triedDerived = false;
          bannerEl.addEventListener('error', () => {
            const derived = bannerEl.getAttribute('data-derived-banner');
            const derivedUrl = derived ? safeUrl(decodeURIComponent(derived)) : null;
            const defaultBanner = 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
            if (!triedDerived && derivedUrl) {
              triedDerived = true;
              bannerEl.src = derivedUrl;
              return;
            }
            if (bannerEl.src !== defaultBanner) bannerEl.src = defaultBanner;
          });
        }
        const logoEl = card.querySelector('img.search-logo');
        if (logoEl) {
          const defaultLogo = 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
          logoEl.addEventListener('error', () => {
            if (logoEl.src !== defaultLogo) logoEl.src = defaultLogo;
          });
        }
      } catch (_) { /* noop */ }

      // Click en tarjeta
      card.addEventListener('click', (e) => {
        try { console.log('👉 [CARD CLICK] negocio:', negocio?.name, { is_premium: negocio?.is_premium }); } catch(_){}
        if (isPremium(negocio)) {
          try { if (e) { e.preventDefault?.(); e.stopPropagation?.(); } } catch(_){}
          try { console.log('✅ [CARD CLICK] premium detectado, abriendo ventana'); } catch(_){}
          import('./premium-card.js').then(m => m.openPremiumWindowForBusiness(negocio)).catch(() => {});
          return;
        }
        import('./utils.js').then(m => {
          if (!m.navigateToBizContext(negocio)) {
            // No hacer nada extra
          }
        }).catch(() => {});
      });
    });

    // Auto-scroll a tarjeta objetivo
    try {
      if (window.__scrollToBizName) {
        const targetNorm = window.__scrollToBizName;
        requestAnimationFrame(() => {
          const cards = Array.from(grid.querySelectorAll('.search-result-card'));
          const targetCard = cards.find(el => (el.getAttribute('data-biz-name-norm') || '') === targetNorm);
          if (targetCard) {
            targetCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const prevOutline = targetCard.style.outline;
            targetCard.style.outline = '2px solid #2563eb';
            setTimeout(() => { targetCard.style.outline = prevOutline || 'none'; }, 1600);
          }
          window.__scrollToBizName = null;
        });
      }
    } catch (_) { /* noop */ }

    // Etiquetas encontradas
    if (etiquetas_encontradas && etiquetas_encontradas.length > 0) {
      const wrap = document.createElement('div');
      wrap.style.cssText = 'margin-top:1rem;color:#1d62a6;font-size:0.98rem;';
      const b = document.createElement('b');
      b.textContent = 'Coincidencias en:';
      wrap.appendChild(b);
      wrap.appendChild(document.createTextNode(' ' + etiquetas_encontradas.join(', ')));
      container.appendChild(wrap);
    }
  } else {
    // Sin resultados
    container.style.display = 'block';
    const msg = document.createElement('div');
    msg.style.cssText = 'color:#f87171;font-weight:bold;font-size:1.1rem;margin-bottom:1rem;';
    msg.textContent = message || 'No se encontraron negocios.';

    const help = document.createElement('div');
    help.style.cssText = 'color:#6b7280;font-size:0.95rem;line-height:1.5;';
    const p = document.createElement('p');
    const strong = document.createElement('strong');
    strong.textContent = 'Intenta buscar:';
    p.appendChild(strong);
    const ul = document.createElement('ul');
    ul.style.cssText = 'margin:0.5rem 0;padding-left:1.5rem;';
    ['Entretenimiento (eventos, música, fotografía)', 'Tecnología (servicios IT, desarrollo)', 'Educación (escuelas, academias)', 'Gastronomía (restaurantes, cafeterías)'].forEach(t => {
      const li = document.createElement('li');
      li.textContent = t;
      ul.appendChild(li);
    });
    help.appendChild(p);
    help.appendChild(ul);
    container.appendChild(msg);
    container.appendChild(help);
  }
}

/**
 * Limpia los resultados mostrados
 */
export function limpiarResultados() {
  const container = getResultadosContainer();
  container.replaceChildren();
  container.style.display = 'none';
}
